/**
 * useChildBreakdown Hook
 *
 * Drives the "Auto-create children" flow on the work item detail page
 * (FLOW-747): asking the AI whether an item warrants breaking down into
 * children, letting the user prune the proposed checklist, and creating the
 * checked ones as real children via the existing create-work-item endpoint.
 */

import { Accessor, createSignal } from "solid-js";
import type {
  ChildBreakdownProposal,
  CreateWorkItemRequest,
  WorkItem,
} from "../api/types";
import { proposeChildren } from "../api/workItems";
import { api } from "../api/client";
import { useToast } from "./useToast";
import { describeAiError, surfaceQuotaExhausted } from "../utils/aiError";

export interface UseChildBreakdownOptions {
  workItemId: Accessor<string>;
  projectId: Accessor<string>;
  childTypeId: Accessor<string | undefined>;
  onCreated: () => void;
}

export interface UseChildBreakdownResult {
  proposal: Accessor<ChildBreakdownProposal | undefined>;
  busy: Accessor<boolean>;
  creating: Accessor<boolean>;
  error: Accessor<string | null>;
  selected: Accessor<Set<number>>;
  selectedCount: Accessor<number>;
  propose: (force: boolean) => Promise<void>;
  toggle: (index: number) => void;
  create: () => Promise<void>;
  dismiss: () => void;
}

export function useChildBreakdown(
  opts: UseChildBreakdownOptions,
): UseChildBreakdownResult {
  const { showSuccess, showError } = useToast();

  const [proposal, setProposal] = createSignal<ChildBreakdownProposal>();
  const [busy, setBusy] = createSignal(false);
  const [creating, setCreating] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [selected, setSelected] = createSignal<Set<number>>(new Set());

  const selectedCount = () => selected().size;

  const propose = async (force: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await proposeChildren(opts.workItemId(), force);
      setProposal(result);
      setSelected(new Set(result.children.map((_, index) => index)));
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (index: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const create = async (): Promise<void> => {
    const current = proposal();
    if (!current) return;

    const typeId = opts.childTypeId();
    if (!typeId) {
      setError("Work item types are still loading, try again");
      return;
    }

    const chosen = current.children
      .map((child, index) => ({ child, index }))
      .filter(({ index }) => selected().has(index));
    if (chosen.length === 0) return;

    setCreating(true);
    setError(null);
    let createdCount = 0;
    try {
      for (const { child, index } of chosen) {
        const payload: CreateWorkItemRequest = {
          title: child.title,
          description: child.description ?? undefined,
          item_type_id: typeId,
          parent_id: opts.workItemId(),
        };
        await api.post<WorkItem>(
          `/projects/${opts.projectId()}/work-items`,
          payload,
        );
        createdCount += 1;
        // Drop it from the selection as it lands, so a retry after a
        // mid-loop failure only re-posts the children that never got created.
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }
      showSuccess(`${createdCount} child items created`);
      setProposal(undefined);
      setSelected(new Set<number>());
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create child items";
      setError(message);
      showError(message);
    } finally {
      setCreating(false);
      if (createdCount > 0) {
        opts.onCreated();
      }
    }
  };

  const dismiss = (): void => {
    setProposal(undefined);
    setError(null);
    setSelected(new Set<number>());
  };

  return {
    proposal,
    busy,
    creating,
    error,
    selected,
    selectedCount,
    propose,
    toggle,
    create,
    dismiss,
  };
}
